import { escapeRegExp } from "@fatbees/utils";
import { propEq, Tuple } from "ramda";
import { useCallback } from "react";
import { useDateTimeSeparators } from "./useDateTimeSeparators";
import { useDayPeriods } from "./useDayPeriods";
import { useLanguage } from "./useLanguage";
import { useMonthNames } from "./useMonthNames";

const indexByType: Partial<Record<keyof Intl.DateTimeFormatOptions, number>> = {
  year: 0,
  month: 1,
  day: 2,
  hour: 3,
  minute: 4,
  second: 5,
};

export function useParseDateWithFormat(): (
  format: Intl.DateTimeFormatOptions,
  text: string,
) => Date | undefined {
  const language = useLanguage();
  const dateTimeSeparators = useDateTimeSeparators();
  const narrowMonthNames = useMonthNames("narrow");
  const shortMonthNames = useMonthNames("short");
  const longMonthNames = useMonthNames("long");
  const dateTimeSeparatorRegexps = [
    ...dateTimeSeparators.map(
      (separator) => `\\s*${escapeRegExp(separator)}\\s*`,
    ),
    "\\s+",
  ];
  const dateTimeSeparatorPattern = `(${dateTimeSeparatorRegexps.join("|")})`;
  const dayPeriods = useDayPeriods();

  return useCallback(
    (format, text = "") => {
      const formatter = new Intl.DateTimeFormat(language, format);
      const parts = formatter.formatToParts();
      const regexpParts = parts.map((part): string => {
        if (part.type === "literal") {
          if (dateTimeSeparators.includes(part.value.trim())) {
            return dateTimeSeparatorPattern;
          } else {
            return `(${escapeRegExp(part.value).replace(/\s+/g, "\\s*")})`;
          }
        } else if (part.type === "year") {
          return "(\\d{4})";
        } else if (part.type === "month") {
          const allowedMonthNames = [
            ...onlyIfUnambiguous(narrowMonthNames),
            ...onlyIfUnambiguous(shortMonthNames),
            ...onlyIfUnambiguous(longMonthNames),
          ];

          return `(${[...allowedMonthNames, "\\d+"].join("|")})`;
        } else if (part.type === "dayPeriod") {
          return `(${dayPeriods.join("|")})`;
        } else {
          return "(\\d{1,2})";
        }
      });
      const regexp = new RegExp(`^${regexpParts.join("")}$`, "i");
      const match = text.toLowerCase().match(regexp);
      const dateParts = match && match.slice(1);
      // Make sure making whitespace optional hasn't caused us to split a year in half
      const hasSplitNumbers =
        dateParts &&
        dateParts
          .filter((part) => part.length > 0)
          .some(
            (part, idx, nonBlankParts) =>
              isNumber(part) && isNumber(nonBlankParts[idx - 1]),
          );

      if (dateParts && !hasSplitNumbers) {
        const now = new Date();
        const dateArgs: Tuple<number, 6> = [
          now.getFullYear(),
          // Default to the start of the year/month for formats like 2023, Dec and Dec 2023
          hasMonthOrYear(parts) ? 0 : now.getMonth(),
          hasMonthOrYear(parts) ? 1 : now.getDate(),
          0,
          0,
          0,
        ];
        const dayPeriodIdx = parts.findIndex(propEq("dayPeriod", "type"));
        const dayPeriodPart =
          dayPeriodIdx !== -1 ? dateParts[dayPeriodIdx] : undefined;
        const isPm =
          dayPeriodPart && dayPeriods[1].toLowerCase() === dayPeriodPart;
        dateParts.forEach((value, partIdx) => {
          const type = parts[partIdx].type;
          let numericValue: number | undefined;

          if (type === "month") {
            const narrowIndex = narrowMonthNames
              .map((name) => name.toLowerCase())
              .indexOf(value);
            const shortIndex = shortMonthNames
              .map((name) => name.toLowerCase())
              .indexOf(value);
            const longIndex = longMonthNames
              .map((name) => name.toLowerCase())
              .indexOf(value);

            if (narrowIndex !== -1) {
              numericValue = narrowIndex;
            } else if (shortIndex !== -1) {
              numericValue = shortIndex; // short months are 1-indexed
            } else if (longIndex !== -1) {
              numericValue = longIndex; // long months are 1-indexed
            } else {
              // JS months are 0-based
              numericValue = Number(value) - 1;
            }
          } else if (type === "hour") {
            const hour = Number(value);

            numericValue = isPm && hour < 12 ? hour + 12 : hour;
          } else if (Object.keys(indexByType).includes(type)) {
            numericValue = Number(value);
          }

          if (numericValue !== undefined && type in indexByType) {
            dateArgs[indexByType[type as keyof Intl.DateTimeFormatOptions]!] =
              numericValue;
          }
        });

        return new Date(...dateArgs);
      } else {
        return undefined;
      }
    },
    [
      language,
      dateTimeSeparators,
      narrowMonthNames,
      shortMonthNames,
      longMonthNames,
      dateTimeSeparatorPattern,
      dayPeriods,
    ],
  );
}

function onlyIfUnambiguous(values: string[]): string[] {
  if (new Set(values).size === values.length) {
    return values;
  } else {
    return [];
  }
}

function isNumber(text: string): boolean {
  return /^\d+$/.test(text);
}

function hasMonthOrYear(parts: readonly Intl.DateTimeFormatPart[]): boolean {
  return parts.some((part) => ["year", "month"].includes(part.type));
}
